'use client'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { CopyButton } from './CopyButton'
import { SyntaxPre } from './syntax-highlight'

type CodeTab = 'curl' | 'python'

const TABS: { id: CodeTab; label: string; language: 'bash' | 'python' }[] = [
  { id: 'curl', label: 'cURL', language: 'bash' },
  { id: 'python', label: 'Python', language: 'python' },
]

interface CodeBlockProps {
  samples: { curl: string; python: string }
  className?: string
}

export function CodeBlock({ samples, className }: CodeBlockProps) {
  const [activeTab, setActiveTab] = useState<CodeTab>('curl')
  const active = TABS.find((t) => t.id === activeTab)!
  const code = samples[activeTab]

  return (
    <div className={cn('overflow-hidden rounded-xl ring-1 ring-black/[0.06]', className)}>
      <div className='flex items-center justify-between border-b border-black/[0.06] bg-white/80 px-2 py-1.5'>
        <div className='flex gap-1' role='tablist' aria-label='Code sample language'>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type='button'
              role='tab'
              aria-selected={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cornflower',
                activeTab === tab.id
                  ? 'bg-brand-cornflower/30 text-brand-navy'
                  : 'text-brand-muted hover:text-brand-navy'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <CopyButton value={code} label='Copy code' />
      </div>
      <SyntaxPre code={code} language={active.language} className='rounded-none ring-0' />
    </div>
  )
}

interface ExampleBlockProps {
  label: string
  code: string
  language?: 'json' | 'http'
}

export function ExampleBlock({ label, code, language = 'json' }: ExampleBlockProps) {
  const isJson = language === 'json'
  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-between gap-2'>
        <p className='text-sm font-medium text-brand-navy'>{label}</p>
        <CopyButton value={code} label={`Copy ${label.toLowerCase()}`} />
      </div>
      <SyntaxPre code={code} language={isJson ? 'json' : 'http'} />
    </div>
  )
}
